import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import ServicePageLayout from '../../components/Layout/ServicePageLayout';
import { FaShoppingCart, FaCreditCard, FaShieldAlt, FaMobile, FaChartLine, FaUsers } from 'react-icons/fa';
import { SiStripe, SiShopify, SiWoocommerce, SiMagento } from 'react-icons/si';
import { MdEmail, MdPhone, MdPerson, MdMessage } from 'react-icons/md';
import { submitToGoogleSheet } from '../../utils/helpers';
import classes from './ServicePage.module.scss';
import contactClasses from '../../components/Contact/Contact.module.scss';
import FAQ from '../../components/FAQ/FAQ';

// Import e-commerce specific feature images
import securePaymentsImg from '../../assets/secure_payments_e_commerce_service.png';
import mobileCommerceImg from '../../assets/mobile_commerce_service.png';
import inventoryMgmtImg from '../../assets/inventory_mgmnt_e_commerce_service.png';
import userManagementImg from '../../assets/user_management_e_commerce_service.png';

const EcommerceDevelopment = () => {
  const { projects } = useSelector((state) => state.portfolio);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitToGoogleSheet(formData);
      if (typeof window.gtag_report_conversion === 'function') {
        window.gtag_report_conversion();
      }
      alert('Thank you! We will get back to you soon.');
      setFormData({ name: '', email: '', phoneNumber: '', message: '' });
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter e-commerce related projects (we'll identify by checking if they have payment integration)
  const ecommerceProjects = projects.filter(project => 
    project.name.toLowerCase().includes('saree') || 
    project.tech.some(tech => tech.toLowerCase().includes('razorpay') || tech.toLowerCase().includes('payment'))
  );

  const technologies = [
    { name: 'Shopify', icon: <SiShopify />, description: 'Custom Shopify stores' },
    { name: 'WooCommerce', icon: <SiWoocommerce />, description: 'WordPress e-commerce' },
    { name: 'Stripe', icon: <SiStripe />, description: 'Payment processing' },
    { name: 'Magento', icon: <SiMagento />, description: 'Enterprise e-commerce' },
  ];

  const features = [
    {
      title: 'Secure Payments',
      description: 'Integrated payment gateways with bank-level security for safe transactions.',
      image: securePaymentsImg
    },
    {
      title: 'Mobile Commerce',
      description: 'Mobile-optimized shopping experience with app-like performance.',
      image: mobileCommerceImg
    },
    {
      title: 'Inventory Management',
      description: 'Real-time inventory tracking with automated stock management.',
      image: inventoryMgmtImg
    },
    {
      title: 'User Management',
      description: 'Complete customer management with order history and preferences.',
      image: userManagementImg
    }
  ];

  const faqs = [
    {
      question: 'Which e-commerce platform is right for my business?',
      answer: 'It depends on your needs. Shopify is ideal for quick launches and small-medium stores. WooCommerce works great if you already use WordPress. For large catalogs and enterprise needs, Magento or a custom React-based solution provides maximum flexibility and performance.'
    },
    {
      question: 'How do you handle payment gateway integration?',
      answer: 'We integrate with Razorpay, Stripe, PayPal, and other payment providers. We handle PCI compliance, support multiple currencies, enable UPI/net banking/cards, and set up recurring payments and refund workflows.'
    },
    {
      question: 'Can you migrate my existing store to a new platform?',
      answer: 'Yes. We migrate products, customers, orders, and SEO data from one platform to another with zero data loss. We handle redirects to preserve search rankings and ensure a seamless transition with minimal downtime.'
    },
    {
      question: 'Do you build custom features like subscription boxes or loyalty programs?',
      answer: 'Absolutely. We build custom features including subscription models, loyalty and reward programs, wishlists, product recommendations, abandoned cart recovery, and advanced discount engines tailored to your business.'
    },
    {
      question: 'How do you ensure the store performs well during high traffic?',
      answer: 'We use CDN, caching strategies, optimized database queries, and auto-scaling cloud infrastructure. We load test before launch and set up monitoring to handle traffic spikes during sales and promotions without downtime.'
    },
    {
      question: 'Do you provide analytics and reporting?',
      answer: 'Yes. We integrate Google Analytics, Facebook Pixel, and custom dashboards that track sales, conversion rates, customer behavior, inventory levels, and revenue — giving you full visibility into your store performance.'
    },
  ];

  const process = [
    { step: '01', title: 'Business Analysis', description: 'Understanding your products, market, and business model' },
    { step: '02', title: 'Platform Selection', description: 'Choosing the right e-commerce platform for your needs' },
    { step: '03', title: 'Store Design', description: 'Creating engaging and conversion-optimized designs' },
    { step: '04', title: 'Development', description: 'Building custom features and payment integrations' },
    { step: '05', title: 'Testing & Launch', description: 'Comprehensive testing and smooth store launch' },
    { step: '06', title: 'Growth Support', description: 'Ongoing optimization and feature enhancements' },
  ];

  return (
    <ServicePageLayout>
      <div className={classes.servicePageContainer}>
        {/* Hero Section */}
        <section className={classes.hero}>
          <div className={classes.container}>
            <div className={classes.heroContent}>
              <div className={classes.heroHighlight}>
                <div className={classes.heroText}>
                  <span className={classes.badge}>E-commerce Development</span>
                <h1 className={classes.heroTitle}>
                  E-commerce <span className={classes.gradient}>Solutions</span>
                </h1>
                <p className={classes.heroSubtitle}>
                  Build powerful online stores that convert visitors into customers. From product catalogs 
                  to secure payments, we create complete e-commerce experiences that drive sales.
                </p>
                <div className={classes.heroCTA}>
                  <button 
                    className={classes.exploreButton}
                    onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                  >
                    Explore Services
                  </button>
                  <button 
                    className={classes.quoteButton}
                    onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
                  >
                    Get A Quote
                  </button>
                </div>
                <div className={classes.heroStats}>
                  <div className={classes.stat}>
                    <span className={classes.statNumber}>{ecommerceProjects.length}+</span>
                    <span className={classes.statLabel}>E-commerce Sites</span>
                  </div>
                  <div className={classes.stat}>
                    <span className={classes.statNumber}>₹10L+</span>
                    <span className={classes.statLabel}>Revenue Generated</span>
                  </div>
                  <div className={classes.stat}>
                    <span className={classes.statNumber}>99.9%</span>
                    <span className={classes.statLabel}>Payment Success</span>
                  </div>
                  </div>
                </div>
              </div>
              <div className={classes.heroVisual}>
                <div className={classes.phoneContainer}>
                  <div className={classes.phoneGlow}></div>
                  <FaShoppingCart className={classes.phoneIcon} />
                  <div className={classes.orbitRing}>
                    <div className={classes.orbitDot}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Technologies Section */}
        <section className={classes.technologies}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                E-commerce <span className={classes.gradient}>Platforms</span>
              </h2>
              <p className={classes.sectionSubtitle}>
                Expert development on leading e-commerce platforms and payment systems
              </p>
            </div>
            <div className={classes.techGrid}>
              {technologies.map((tech, index) => (
                <div key={index} className={classes.techCard}>
                  <div className={classes.techIcon}>{tech.icon}</div>
                  <h3 className={classes.techName}>{tech.name}</h3>
                  <p className={classes.techDescription}>{tech.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className={classes.features}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                Complete <span className={classes.gradient}>E-commerce Features</span>
              </h2>
            </div>
            <div className={classes.featuresGrid}>
              {features.map((feature, index) => (
                <div key={index} className={classes.featureCard}>
                  <div className={classes.featureImageContainer}>
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className={classes.featureImage}
                    />
                  </div>
                  <h3 className={classes.featureTitle}>{feature.title}</h3>
                  <p className={classes.featureDescription}>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <section className={classes.projects}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                E-commerce <span className={classes.gradient}>Success Stories</span>
              </h2>
              <p className={classes.sectionSubtitle}>
                Successful online stores generating real revenue for businesses
              </p>
            </div>
            <div className={classes.projectsGrid}>
              {ecommerceProjects.map((project) => (
                <div key={project.id} className={classes.projectCard}>
                  <div className={classes.projectHeader}>
                    <FaShoppingCart className={classes.projectIcon} />
                    {project.featured && (
                      <span className={classes.featuredBadge}>Featured</span>
                    )}
                  </div>
                  <h3 className={classes.projectName}>{project.name}</h3>
                  <p className={classes.projectDescription}>{project.description}</p>
                  <div className={classes.projectTech}>
                    {project.tech.slice(0, 3).map((tech, index) => (
                      <span key={index} className={classes.techTag}>{tech}</span>
                    ))}
                    {project.tech.length > 3 && (
                      <span className={classes.techTag}>+{project.tech.length - 3}</span>
                    )}
                  </div>
                  <a 
                    href={project.url} 
                    className={classes.projectLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    View Store
                  </a>
                </div>
              ))}
              
              {/* If no e-commerce projects found, show placeholder */}
              {ecommerceProjects.length === 0 && (
                <div className={classes.projectCard}>
                  <div className={classes.projectHeader}>
                    <FaShoppingCart className={classes.projectIcon} />
                  </div>
                  <h3 className={classes.projectName}>Custom E-commerce Store</h3>
                  <p className={classes.projectDescription}>
                    Full-featured online store with secure payments, inventory management, and customer portal.
                  </p>
                  <div className={classes.projectTech}>
                    <span className={classes.techTag}>React</span>
                    <span className={classes.techTag}>Node.js</span>
                    <span className={classes.techTag}>Stripe</span>
                  </div>
                  <a href="#contact" className={classes.projectLink}>
                    Start Your Store
                  </a>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className={classes.process}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                Our E-commerce <span className={classes.gradient}>Process</span>
              </h2>
              <p className={classes.sectionSubtitle}>
                Proven methodology for launching successful online stores
              </p>
            </div>
            <div className={classes.processGrid}>
              {process.map((step, index) => (
                <div key={index} className={classes.processStep}>
                  <div className={classes.stepNumber}>{step.step}</div>
                  <h3 className={classes.stepTitle}>{step.title}</h3>
                  <p className={classes.stepDescription}>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <FAQ faqs={faqs} title="Frequently Asked" highlight="Questions" />

        {/* Contact Form Section */}
        <section id="contact-form" className={contactClasses.contact}>
          <div className={contactClasses.container}>
            <div className={contactClasses.header}>
              <span className={contactClasses.badge}>Get Your Quote</span>
              <h2 className={contactClasses.title}>
                Let's Build Your <span className={contactClasses.gradient}>Online Store</span>
              </h2>
            </div>

            <div className={contactClasses.content}>
              <form className={contactClasses.form} onSubmit={handleSubmit}>
                <div className={contactClasses.formRow}>
                  <div className={contactClasses.formGroup}>
                    <label htmlFor="name">
                      <MdPerson className={contactClasses.labelIcon} />
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your full name"
                      className={contactClasses.premiumInput}
                    />
                  </div>

                  <div className={contactClasses.formGroup}>
                    <label htmlFor="email">
                      <MdEmail className={contactClasses.labelIcon} />
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@company.com"
                      className={contactClasses.premiumInput}
                    />
                  </div>
                </div>

                <div className={contactClasses.formGroup}>
                  <label htmlFor="phoneNumber">
                    <MdPhone className={contactClasses.labelIcon} />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    required
                    placeholder="+1 (555) 123-4567"
                    className={contactClasses.premiumInput}
                  />
                </div>

                <div className={contactClasses.formGroup}>
                  <label htmlFor="message">
                    <MdMessage className={contactClasses.labelIcon} />
                    E-commerce Project Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Tell us about your e-commerce requirements - product types, target audience, payment integrations, timeline, and any specific features..."
                    className={contactClasses.premiumTextarea}
                  ></textarea>
                </div>

                <button type="submit" className={contactClasses.submitBtn} disabled={loading}>
                  <span className={contactClasses.btnText}>
                    {loading && <span className={contactClasses.spinner} />}
                    {loading ? 'Sending...' : 'Get My Quote'}
                  </span>
                  <div className={contactClasses.btnGlow}></div>
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={classes.cta}>
          <div className={classes.container}>
            <div className={classes.ctaContent}>
              <h2 className={classes.ctaTitle}>
                Ready to Launch Your <span className={classes.gradient}>Online Store</span>?
              </h2>
              <p className={classes.ctaSubtitle}>
                Let's build an e-commerce platform that turns visitors into loyal customers
              </p>
              <a href="#contact-form" className={classes.ctaButton}>Start Selling Online</a>
            </div>
          </div>
        </section>
      </div>
    </ServicePageLayout>
  );
};

export default EcommerceDevelopment;